import styled from 'styled-components';

import { findColorKey, pxToRem } from '../../../utils/styles';
import Icon from '../Icon';
import { CustomConfiguration } from './types';

type ButtonProps = {
  $buttonTheme: 'dark' | 'light';
  $customConfiguration?: CustomConfiguration;
  $hasHover: boolean;
  $hasIconOnly: boolean;
  $hasNoPadding?: boolean;
  $isIconFilled?: boolean;
};

const getPadding = ($hasIconOnly: any, $hasNoPadding: any) => {
  if ($hasNoPadding) {
    return 'initial';
  }

  if (!$hasIconOnly) {
    return `${pxToRem(12)} ${pxToRem(24)}`;
  }

  return `${pxToRem(12)}`;
};

const getHeight = ($customConfiguration: any) => {
  if (!$customConfiguration?.height) {
    return `${pxToRem(48)}`;
  }

  if (typeof $customConfiguration.height === 'number') {
    return pxToRem($customConfiguration.height);
  }

  return $customConfiguration.height;
};

const getWidth = ($customConfiguration: any) => {
  if (!$customConfiguration?.width) {
    return 'auto';
  }

  if (typeof $customConfiguration.width === 'number') {
    return pxToRem($customConfiguration.width);
  }

  return $customConfiguration.width;
};

export const ButtonWrapper = styled.div<{
  $customConfiguration: CustomConfiguration;
}>`
  &&& {
    position: ${({ $customConfiguration }) =>
      !$customConfiguration?.position
        ? 'relative'
        : $customConfiguration?.position};
    width: ${({ $customConfiguration }) => getWidth($customConfiguration)};
  }
`;

export const CustomIcon = styled(Icon)``;

export const CornerIcon = styled(Icon)<{ customsize: number }>`
  &&& {
    position: absolute;
    top: ${({ customsize }) => pxToRem(-customsize / 2)};
    right: ${({ customsize }) => pxToRem(-customsize / 2)};
  }
`;

export const BaseButton = styled.button<ButtonProps>`
  &&& {
    width: ${({ $customConfiguration }) => getWidth($customConfiguration)};
    height: ${({ $customConfiguration }) => getHeight($customConfiguration)};

    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    padding: ${({ $hasIconOnly, $hasNoPadding }) =>
      getPadding($hasIconOnly, $hasNoPadding)};
    margin: 0;
    gap: ${pxToRem(8)};
    flex: none;
    order: 0;
    text-align: ${({ $customConfiguration }) =>
      $customConfiguration?.textAlign};
    flex-grow: 0;
    border: none;
    cursor: ${({ $customConfiguration, disabled }) =>
      $customConfiguration?.cursor
        ? $customConfiguration?.cursor
        : !disabled && 'pointer'};
    transition: all 0.2s;
    border-radius: ${({ theme: { themeName } }) =>
      ['fiat', 'citroen', 'ds'].includes(themeName) ? `${pxToRem(70)}` : `0`};
  }
`;

export const AccentButton = styled(BaseButton)<ButtonProps>`
  &&& {
    background: ${({ $buttonTheme, theme: { colors } }) =>
      colors[findColorKey('lightAccentDefault', $buttonTheme)]};
    color: ${({ $buttonTheme, theme: { colors } }) =>
      colors[findColorKey('textLightAccentDefault', $buttonTheme)]};

    ${CustomIcon} {
      cursor: ${({ disabled }) => !disabled && 'pointer'};

      path {
        fill: ${({
          $buttonTheme,
          $hasHover,
          $isIconFilled,
          theme: { colors }
        }) =>
          $isIconFilled &&
          $hasHover &&
          colors[findColorKey('textLightAccentDefault', $buttonTheme)]};
      }
    }

    &:hover {
      background: ${({ $buttonTheme, $hasHover, theme: { colors } }) =>
        $hasHover && colors[findColorKey('lightAccentHover', $buttonTheme)]};
      color: ${({ $buttonTheme, $hasHover, theme: { colors } }) =>
        $hasHover &&
        colors[findColorKey('textLightAccentHover', $buttonTheme)]};

      ${CustomIcon} {
        path {
          fill: ${({
            $buttonTheme,
            $hasHover,
            $isIconFilled,
            theme: { colors }
          }) =>
            $isIconFilled &&
            $hasHover &&
            colors[findColorKey('textLightAccentHover', $buttonTheme)]};
        }
      }
    }

    &:disabled {
      background: ${({ $buttonTheme, theme: { colors } }) =>
        colors[findColorKey('lightAccentDisabled', $buttonTheme)]};
      color: ${({ $buttonTheme, theme: { colors } }) =>
        colors[findColorKey('textLightAccentDisabled', $buttonTheme)]};

      ${CustomIcon} {
        path {
          fill: ${({
            $buttonTheme,
            $hasHover,
            $isIconFilled,
            theme: { colors }
          }) =>
            $isIconFilled &&
            $hasHover &&
            colors[findColorKey('textLightAccentDisabled', $buttonTheme)]};
        }
      }
    }
  }
`;

export const PrimaryButton = styled(BaseButton)<ButtonProps>`
  &&& {
    background: ${({ $buttonTheme, theme: { colors } }) =>
      colors[findColorKey('light1Default', $buttonTheme)]};
    color: ${({ $buttonTheme, theme: { colors } }) =>
      colors[findColorKey('textLight1Default', $buttonTheme)]};

    ${CustomIcon} {
      cursor: ${({ disabled }) => !disabled && 'pointer'};

      path {
        fill: ${({
          $buttonTheme,
          $hasHover,
          $isIconFilled,
          theme: { colors }
        }) =>
          $isIconFilled &&
          $hasHover &&
          colors[findColorKey('textLight1Default', $buttonTheme)]};
      }
    }

    &:hover {
      background: ${({ $buttonTheme, $hasHover, theme: { colors } }) =>
        $hasHover && colors[findColorKey('light1Hover', $buttonTheme)]};
      color: ${({ $buttonTheme, $hasHover, theme: { colors } }) =>
        $hasHover && colors[findColorKey('textLight1Hover', $buttonTheme)]};

      ${CustomIcon} {
        path {
          fill: ${({
            $buttonTheme,
            $hasHover,
            $isIconFilled,
            theme: { colors }
          }) =>
            $isIconFilled &&
            $hasHover &&
            colors[findColorKey('textLight1Hover', $buttonTheme)]};
        }
      }
    }

    &:disabled {
      background: ${({ $buttonTheme, theme: { colors } }) =>
        colors[findColorKey('light1Disabled', $buttonTheme)]};
      color: ${({ $buttonTheme, theme: { colors } }) =>
        colors[findColorKey('textLight1Disabled', $buttonTheme)]};

      ${CustomIcon} {
        path {
          fill: ${({
            $buttonTheme,
            $hasHover,
            $isIconFilled,
            theme: { colors }
          }) =>
            $isIconFilled &&
            $hasHover &&
            colors[findColorKey('textLight1Disabled', $buttonTheme)]};
        }
      }
    }
  }
`;

export const SecondaryButton = styled(BaseButton)<ButtonProps>`
  &&& {
    background: transparent;
    border: ${pxToRem(1)} solid
      ${({ $buttonTheme, theme: { colors } }) =>
        colors[findColorKey('textLight2Default', $buttonTheme)]};

    color: ${({ $buttonTheme, theme: { colors } }) =>
      colors[findColorKey('textLight2Default', $buttonTheme)]};

    ${CustomIcon} {
      cursor: ${({ disabled }) => !disabled && 'pointer'};

      path {
        fill: ${({
          $buttonTheme,
          $hasHover,
          $isIconFilled,
          theme: { colors }
        }) =>
          $isIconFilled &&
          $hasHover &&
          colors[findColorKey('textLight2Default', $buttonTheme)]};
      }
    }

    &:hover {
      border: ${pxToRem(1)} solid
        ${({ $buttonTheme, $hasHover, theme: { colors } }) =>
          $hasHover && colors[findColorKey('textLight2Hover', $buttonTheme)]};
      color: ${({ $buttonTheme, $hasHover, theme: { colors } }) =>
        $hasHover && colors[findColorKey('textLight2Hover', $buttonTheme)]};

      ${CustomIcon} {
        path {
          fill: ${({
            $buttonTheme,
            $hasHover,
            $isIconFilled,
            theme: { colors }
          }) =>
            $isIconFilled &&
            $hasHover &&
            colors[findColorKey('textLight2Hover', $buttonTheme)]};
        }
      }
    }

    &:disabled {
      border: ${pxToRem(1)} solid
        ${({ $buttonTheme, theme: { colors } }) =>
          colors[findColorKey('textLight2Disabled', $buttonTheme)]};
      color: ${({ $buttonTheme, theme: { colors } }) =>
        colors[findColorKey('textLight2Disabled', $buttonTheme)]};

      ${CustomIcon} {
        path {
          fill: ${({
            $buttonTheme,
            $hasHover,
            $isIconFilled,
            theme: { colors }
          }) =>
            $isIconFilled &&
            $hasHover &&
            colors[findColorKey('textLight2Disabled', $buttonTheme)]};
        }
      }
    }
  }
`;

export const TertiaryButton = styled(BaseButton)<ButtonProps>`
  &&& {
    background: transparent;
    color: ${({ $buttonTheme, theme: { colors } }) =>
      colors[findColorKey('textLight2Default', $buttonTheme)]};

    ${CustomIcon} {
      cursor: ${({ disabled }) => !disabled && 'pointer'};
      path {
        fill: ${({
          $buttonTheme,
          $hasHover,
          $isIconFilled,
          theme: { colors }
        }) =>
          $isIconFilled &&
          $hasHover &&
          colors[findColorKey('textLight2Default', $buttonTheme)]};
      }
    }

    &:hover {
      color: ${({ $buttonTheme, $hasHover, theme: { colors } }) =>
        $hasHover && colors[findColorKey('textLight2Hover', $buttonTheme)]};

      ${CustomIcon} {
        path {
          fill: ${({
            $buttonTheme,
            $hasHover,
            $isIconFilled,
            theme: { colors }
          }) =>
            $isIconFilled &&
            $hasHover &&
            colors[findColorKey('textLight2Hover', $buttonTheme)]};
        }
      }
    }

    &:disabled {
      color: ${({ $buttonTheme, theme: { colors } }) =>
        colors[findColorKey('textLight2Disabled', $buttonTheme)]};

      ${CustomIcon} {
        path {
          fill: ${({
            $buttonTheme,
            $hasHover,
            $isIconFilled,
            theme: { colors }
          }) =>
            $isIconFilled &&
            $hasHover &&
            colors[findColorKey('textLight2Disabled', $buttonTheme)]};
        }
      }
    }
  }
`;

export const QuaternaryButton = styled(BaseButton)<ButtonProps>`
  &&& {
    background: transparent;
    border: ${pxToRem(1)} solid
      ${({ $buttonTheme, theme: { colors } }) =>
        colors[findColorKey('brand2', $buttonTheme)]};

    color: ${({ $buttonTheme, theme: { colors } }) =>
      colors[findColorKey('brand2', $buttonTheme)]};

    ${CustomIcon} {
      cursor: ${({ disabled }) => !disabled && 'pointer'};

      path {
        fill: ${({
          $buttonTheme,
          $hasHover,
          $isIconFilled,
          theme: { colors }
        }) =>
          $isIconFilled &&
          $hasHover &&
          colors[findColorKey('brand2', $buttonTheme)]};
      }
    }

    &:hover {
      border: ${pxToRem(1)} solid
        ${({ $buttonTheme, $hasHover, theme: { colors } }) =>
          $hasHover && colors[findColorKey('lightAccentHover', $buttonTheme)]};
      color: ${({ $buttonTheme, $hasHover, theme: { colors } }) =>
        $hasHover && colors[findColorKey('lightAccentHover', $buttonTheme)]};

      ${CustomIcon} {
        path {
          fill: ${({
            $buttonTheme,
            $hasHover,
            $isIconFilled,
            theme: { colors }
          }) =>
            $isIconFilled &&
            $hasHover &&
            colors[findColorKey('lightAccentHover', $buttonTheme)]};
        }
      }
    }

    &:disabled {
      border: ${pxToRem(1)} solid
        ${({ $buttonTheme, theme: { colors } }) =>
          colors[findColorKey('light2Disabled', $buttonTheme)]};
      color: ${({ $buttonTheme, theme: { colors } }) =>
        colors[findColorKey('textLight2Disabled', $buttonTheme)]};

      ${CustomIcon} {
        path {
          fill: ${({
            $buttonTheme,
            $hasHover,
            $isIconFilled,
            theme: { colors }
          }) =>
            $isIconFilled &&
            $hasHover &&
            colors[findColorKey('textLight2Disabled', $buttonTheme)]};
        }
      }
    }
  }
`;

export const ButtonLink = styled.button<{
  $customConfiguration: CustomConfiguration;
}>`
  &&& {
    width: ${({
      $customConfiguration
    }: {
      $customConfiguration: CustomConfiguration;
    }) => getWidth($customConfiguration)};
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    gap: ${pxToRem(4)};
    text-align: center;
    background: none;
    border: none;
    cursor: ${({ $customConfiguration, disabled }) =>
      $customConfiguration?.cursor
        ? $customConfiguration?.cursor
        : !disabled && 'pointer'};
    color: ${({ $customConfiguration, theme: { colors } }) =>
      colors[$customConfiguration?.color || 'black']};
  }
`;
